import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { delay } from '@/lib/motion'
import { Ambient } from '@/components/generals/backdrops'
import { ButtonLink } from '@/components/generals/button-link'

interface HeroAction {
    label: string
    href: string
}

interface PageHeroProps {
    title: ReactNode
    sub: string
    primary: HeroAction
    secondary?: HeroAction
    /** Anything under the buttons: a film, a ticker. */
    children?: ReactNode
    /** Decorations around the headline (desktop only), drawn behind the copy. */
    aside?: ReactNode
    /**
     * field: the sky field fills the hero, which holds a minimum height (placements, scouts).
     * ambient: the blobs carry their own wash and the hero sizes to its content (how it works).
     */
    variant?: 'field' | 'ambient'
    titleClassName?: string
    subClassName?: string
}

/** The shared inner-page hero: a breathing sky field, a centred headline, one quiet line, two pills. */
export const PageHero: FC<PageHeroProps> = ({
    title,
    sub,
    primary,
    secondary,
    children,
    aside,
    variant = 'field',
    titleClassName,
    subClassName
}): ReactNode => {
    return (
        <section
            id='hero'
            className={cn(
                'pt-hero section-x relative isolate flex flex-col items-center overflow-hidden text-center',
                variant === 'field' ? 'hero-field min-h-[calc(64px+clamp(480px,45.4vw,620px))] pb-[clamp(40px,4vw,56px)]' : 'pb-[clamp(56px,6vw,88px)]'
            )}
        >
            <Ambient field={variant === 'ambient'} className='-z-10' />
            {aside}

            <div className='relative z-[2] flex w-full flex-col items-center'>
                <h1 className={cn('type-hero enter mx-auto max-w-[18ch]', titleClassName)} style={delay(0)}>
                    {title}
                </h1>

                <p className={cn('type-lead enter mx-auto mt-6 max-w-[52ch]', subClassName)} style={delay(100)}>
                    {sub}
                </p>

                <div className='enter mt-7 flex w-full flex-col items-stretch gap-2.5 sm:w-auto sm:flex-row sm:items-center' style={delay(200)}>
                    <ButtonLink href={primary.href} size='lg' className='max-sm:h-10'>
                        {primary.label}
                    </ButtonLink>

                    {secondary && (
                        <ButtonLink href={secondary.href} size='lg' variant='ghost' className='max-sm:h-10'>
                            {secondary.label}
                        </ButtonLink>
                    )}
                </div>
            </div>

            {children}
        </section>
    )
}
